import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Ban } from "lucide-react";

const EMPTY = { name: "", contact_person: "", email: "", phone: "", category: "", state: "", gstin: "", status: "active", blacklist_reason: "" };

export default function Vendors() {
    const { isAdmin, hasPerm } = useAuth();
    const [items, setItems] = useState([]);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [editId, setEditId] = useState(null);
    const [err, setErr] = useState("");

    const load = async () => { const { data } = await api.get("/vendors"); setItems(data); };
    useEffect(() => { load(); }, []);

    const startEdit = (v) => { setForm({ ...EMPTY, ...v }); setEditId(v.id); setShow(true); };
    const submit = async (e) => {
        e.preventDefault(); setErr("");
        try {
            if (editId) await api.put(`/vendors/${editId}`, form);
            else await api.post("/vendors", form);
            setShow(false); setForm(EMPTY); setEditId(null); load();
        } catch (err) { setErr(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    };
    const blacklist = async (v) => {
        const reason = prompt(`Blacklist ${v.name}? Enter reason:`);
        if (reason == null) return;
        try { await api.post(`/vendors/${v.id}/blacklist`, null, { params: { reason } }); load(); }
        catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    return (
        <div data-testid="vendors-page" className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">Master data</div>
                    <h1 className="text-3xl font-heading font-semibold text-stone-900">Vendors</h1>
                </div>
                {hasPerm("vendor.manage") && (
                    <button data-testid="new-vendor-btn" onClick={() => { setForm(EMPTY); setEditId(null); setShow(true); }} className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2"><Plus size={15} /> New vendor</button>
                )}
            </div>

            {!hasPerm("vendor.manage") && (
                <div className="bg-stone-100 border border-stone-200 rounded-xl p-4 text-sm text-stone-700">
                    <span className="font-medium">Read-only view.</span> Vendors are managed by the Administrator and Full Access users. You see only the vendor(s) assigned to you across your projects.
                </div>
            )}

            <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                            <th className="text-left py-3 px-4">Name</th>
                            <th className="text-left py-3 px-4">Category</th>
                            <th className="text-left py-3 px-4">State</th>
                            <th className="text-left py-3 px-4">GSTIN</th>
                            <th className="text-left py-3 px-4">Contact</th>
                            <th className="text-left py-3 px-4">Status</th>
                            <th className="text-right py-3 px-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(v => (
                            <tr key={v.id} className="border-t border-stone-100 hover:bg-stone-50">
                                <td className="py-3 px-4 font-medium text-stone-900">{v.name}</td>
                                <td className="py-3 px-4 text-stone-700">{v.category || "—"}</td>
                                <td className="py-3 px-4 text-stone-700">{v.state || "—"}</td>
                                <td className="py-3 px-4 font-mono text-xs text-stone-600">{v.gstin || "—"}</td>
                                <td className="py-3 px-4 text-stone-700">
                                    <div>{v.contact_person}</div>
                                    <div className="text-xs text-stone-500">{v.email}</div>
                                </td>
                                <td className="py-3 px-4">
                                    <span className={"text-xs px-2 py-0.5 rounded-md border capitalize " + (
                                        v.status === "blacklisted" ? "bg-red-50 border-red-200 text-red-700" :
                                            v.status === "inactive" ? "bg-stone-100 border-stone-200 text-stone-500" : "bg-green-50 border-green-200 text-green-700"
                                    )}>{v.status}</span>
                                </td>
                                <td className="py-3 px-4 text-right">
                                    {hasPerm("vendor.manage") && <button data-testid={`edit-vendor-${v.id}`} onClick={() => startEdit(v)} className="text-xs text-amber-700 hover:text-amber-900 mr-3">Edit</button>}
                                    {isAdmin() && v.status !== "blacklisted" && <button onClick={() => blacklist(v)} className="text-xs text-red-700 hover:text-red-900 inline-flex items-center gap-1"><Ban size={11} /> Blacklist</button>}
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-stone-500 text-sm">No vendors yet.</td></tr>}
                    </tbody>
                </table>
            </div>

            {show && (
                <div className="fixed inset-0 z-40 bg-stone-900/40 flex items-center justify-center p-4">
                    <div className="bg-white border border-stone-200 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
                            <div className="font-heading font-semibold text-stone-900">{editId ? "Edit vendor" : "New vendor"}</div>
                            <button onClick={() => setShow(false)} className="text-stone-500"><X size={18} /></button>
                        </div>
                        <form onSubmit={submit} className="p-6 space-y-4">
                            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
                            <div className="grid md:grid-cols-2 gap-4">
                                <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="v-name" />
                                <Field label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} />
                                <Field label="Contact person" value={form.contact_person} onChange={(v) => setForm({ ...form, contact_person: v })} />
                                <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                                <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                                <Field label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
                                <Field label="GSTIN" value={form.gstin} onChange={(v) => setForm({ ...form, gstin: v })} />
                                <div>
                                    <label className="overline block mb-1">Status</label>
                                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3">
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                        {isAdmin() && <option value="blacklisted">Blacklisted</option>}
                                    </select>
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => setShow(false)} className="text-sm text-stone-600 px-4 py-2 rounded-md">Cancel</button>
                                <button data-testid="save-vendor" type="submit" className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm font-medium">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
    return (
        <div>
            <label className="overline block mb-1">{label}{required && " *"}</label>
            <input data-testid={testid} type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
        </div>
    );
}
